import {
  StatHandler,
  UserInfo,
  StatEvent,
  TrackParams,
  PageParams,
  UserIds,
  StatContextPage,
  StatWorker,
  WorkerData,
  StatFlags,
} from './StatTypes'
import * as Stat from './Stat'

export type WebStatOptions = {
  apiHost: string
  library: string
  version: string

  batches?: {
    flushAt: number
    flushInterval?: number
  }

  worker?: {
    url: string
  }

  weAreOnCDN?(): boolean

  retryPromise?<T = undefined>(
    promiseWrapper: () => Promise<T>,
    partialOptions?: Partial<{
      maxTries: number
      retryTimeout: number
      increaseTimeoutFactor: number
      maxRetryTimeout: number
      logErrorsToConsole: boolean
      waitUntilOnline: boolean
    }>,
  ): Promise<T>

  addCommonStatParams(params: Record<string, unknown>): Record<string, unknown>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sendError(error: any, extra: any): void
}

type WebStatState = {
  handlers: StatHandler[]

  workerCreationIsInProgress: boolean
  worker: Worker | undefined
  workerDiedTimes: number
}

const USER_ID_COOKIE_NAME = 'ajs_user_id'
const ANONYMOUS_ID_COOKIE_NAME = 'ajs_anonymous_id'

const STATE: WebStatState = {
  handlers: [],
  workerCreationIsInProgress: false,
  worker: undefined,
  workerDiedTimes: 0,
}

export function identify(statHandler: StatHandler, apiKey: string, user: UserInfo): void {
  Stat.identify(statHandler, apiKey, user)
}

export function track(
  statHandler: StatHandler,
  apiKey: string,
  event: string,
  properties?: TrackParams['properties'],
  flags?: StatFlags,
): void {
  Stat.track(statHandler, apiKey, event, properties, flags)
}

export function trackBatch(
  statHandler: StatHandler,
  apiKey: string,
  events: StatEvent[],
  flags?: StatFlags,
): void {
  Stat.trackBatch(statHandler, apiKey, events, flags)
}

export function page(
  statHandler: StatHandler,
  apiKey: string,
  properties?: PageParams['properties'],
  flags?: StatFlags,
): void {
  Stat.page(statHandler, apiKey, properties, flags)
}

export function clearBatches(statHandler: StatHandler): void {
  Stat.clearBatches(statHandler)
}

export function flushBatches(statHandler: StatHandler): void {
  Stat.flushBatches(statHandler)
}

export function flushBatch(statHandler: StatHandler, apiKey: string): void {
  Stat.flushBatch(statHandler, apiKey)
}

export function createStatHandler(options: WebStatOptions): StatHandler {
  const statHandler = Stat.createStatHandler({
    apiHost: options.apiHost,
    library: options.library,
    version: options.version,
    batches: options.batches,
    app: {
      getStoredUserIds: getStoredUserIds,
      storeUserIds: storeUserIds,
      addCommonStatParams(params: Record<string, unknown>): Record<string, unknown> {
        return options.addCommonStatParams(params)
      },
      getStatContextPage: getStatContextPage,
      getUserAgent: getUserAgent,

      sendError: options.sendError,
    },
  })

  const allowWorker = !!options.worker
  if (allowWorker && window.Worker) {
    tryCreateStatsWorker(STATE, options)
  }

  if (options.batches?.flushInterval) {
    statHandler.flushBatchesIntervalId = window.setInterval(() => {
      flushBatches(statHandler)
    }, options.batches.flushInterval)
  }

  STATE.handlers.push(statHandler)
  return statHandler
}

export function destroyStatHandler(statHandler: StatHandler): void {
  clearInterval(statHandler.flushBatchesIntervalId)
  statHandler.flushBatchesIntervalId = undefined

  Stat.destroyStatHandler(statHandler)
}

function tryCreateStatsWorker(state: WebStatState, options: WebStatOptions): void {
  if (!state.worker && !state.workerCreationIsInProgress && options.worker) {
    state.workerCreationIsInProgress = true

    if (state.workerDiedTimes > 5) {
      options.sendError('Stats worker dies too often', {count: state.workerDiedTimes})
      if (state.workerDiedTimes > 10) {
        // NOTE: here we just forget about worker, it dies too often.
        // We keep _workerCreationIsInProgress_ flag
        // forever so no one will be able to create it again
        return
      }
    }

    let workerUrl = options.worker.url
    const weAreOnCDN = options.weAreOnCDN ? options.weAreOnCDN() : false
    if (weAreOnCDN) {
      // We need to use this workaround to be able to specify origin.
      // https://stackoverflow.com/a/60252783
      const workerBlob = new Blob(['importScripts(' + JSON.stringify(workerUrl) + ')'], {
        type: 'application/javascript',
      })
      workerUrl = window.URL.createObjectURL(workerBlob)
    }

    const cleanUpAndTryToRestartWebWorker = (message?: string, error?: string | ErrorEvent) => {
      state.workerDiedTimes++

      if (message) {
        error = error || 'Unknown'
        const extra = typeof error === 'string' ? error : getMessageFromWorkerError(error)
        const customError = new Error(message)
        options.sendError(customError, extra)
      }

      if (weAreOnCDN) {
        window.URL.revokeObjectURL(workerUrl)
      }
      tryCreateStatsWorker(state, options)
    }

    const createWorkerPromise = () =>
      new Promise<void>((resolve, reject) => {
        const worker = new Worker(workerUrl, {name: 'StatsWorker'})
        const timeoutId = setTimeout(() => reject(new Error('timeout')), 10000)
        const onInitError = (error: ErrorEvent) => {
          worker.terminate()
          reject(error)
        }
        const onReady = (event: MessageEvent) => {
          if (event.data === 'ready') {
            clearTimeout(timeoutId)

            worker.removeEventListener('message', onReady)
            worker.removeEventListener('error', onInitError)

            worker.addEventListener('error', error => {
              worker.terminate()
              state.handlers.forEach(handler => {
                handler.worker = undefined
              })
              state.worker = undefined

              cleanUpAndTryToRestartWebWorker('Stats worker died 2', error)
            })
            const statWorker = createStatWorker(worker)
            state.handlers.forEach(handler => {
              handler.worker = statWorker
            })
            state.worker = worker
            state.workerCreationIsInProgress = false
            resolve()
          }
        }
        worker.addEventListener('message', onReady)
        worker.addEventListener('error', onInitError)
      })

    let workerPromise: Promise<unknown>
    if (options.retryPromise) {
      workerPromise = options.retryPromise(createWorkerPromise, {
        maxTries: 5,
        retryTimeout: 3000,
        increaseTimeoutFactor: 1,
        maxRetryTimeout: 5000,
        logErrorsToConsole: true,
        waitUntilOnline: true,
      })
    } else {
      workerPromise = createWorkerPromise()
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    workerPromise.catch((error: any) => {
      // NOTE: we don't care about init errors. We logged them previously,
      // but they were just eating space in errors tracker system
      cleanUpAndTryToRestartWebWorker()
    })
  }
}

function createStatWorker(worker: Worker): StatWorker {
  return {
    postMessage: (data: WorkerData) => {
      switch (data.event) {
        case 'track':
          {
            const bufferData = data.payload.properties?.__bufferData
            if (bufferData && bufferData.transferables) {
              worker.postMessage(data, bufferData.transferables)
            } else {
              worker.postMessage(data)
            }
          }
          break

        case 'trackBatch':
          {
            let transferables: Transferable[] = []
            data.payload.tracks.forEach(track => {
              const bufferData = track.properties?.__bufferData
              if (bufferData && bufferData.transferables) {
                transferables = transferables.concat(bufferData.transferables)
              }
            })
            worker.postMessage(data, transferables)
          }
          break

        default:
          {
            worker.postMessage(data)
          }
          break
      }
    },
  }
}

function getMessageFromWorkerError(error: ErrorEvent): string {
  let message: string
  try {
    message = `Message: ${error.message}. Filename: ${error.filename}. Colno: ${
      error.colno
    }. Rowno: ${error.lineno}. Error: ${String(error.error)}`
  } catch (e) {
    message = `Failed to create message`
  }

  return message
}

function getStoredUserIds(): Partial<UserIds> {
  const cookie = document.cookie

  let anonymousId = getCookieFrom(ANONYMOUS_ID_COOKIE_NAME, cookie)
  if (anonymousId) {
    anonymousId = anonymousId.replace(/"/g, '')
  } else {
    anonymousId = undefined
  }

  let userId = getCookieFrom(USER_ID_COOKIE_NAME, cookie)
  if (userId) {
    userId = userId.replace(/"/g, '')
  } else {
    userId = undefined
  }
  // NOTE: analytics.js writes "null" as a string to cookies..
  if (userId === 'null') {
    userId = undefined
  }
  if (userId === 'undefined') {
    userId = undefined
  }

  return {userId, anonymousId}
}

function storeUserIds(userIds: UserIds): void {
  const date = new Date()
  date.setFullYear(date.getFullYear() + 1)
  setCookie(ANONYMOUS_ID_COOKIE_NAME, `"${userIds.anonymousId}"`, {
    expires: date,
    path: '/',
  })
  if (userIds.userId) {
    setCookie(USER_ID_COOKIE_NAME, `"${userIds.userId}"`, {
      expires: date,
      path: '/',
    })
  } else {
    deleteCookie(USER_ID_COOKIE_NAME, {
      path: '/',
    })
  }
}

function getStatContextPage(statHandler: StatHandler): StatContextPage {
  const context: StatContextPage = {
    referrer: document.referrer,
    path: location.pathname,
    title: document.title,
    url: location.href,
    search: location.search,
    site_language: document.getElementsByTagName('html')[0].getAttribute('lang') || 'en',
    inIframe: inIframe(),
  }
  return context
}

function inIframe(): boolean {
  try {
    return window.self !== window.top
  } catch (e) {
    // blocked by cors
    return true
  }
}

function getUserAgent(): string {
  return navigator.userAgent
}

type Options = {
  expires: Date | string
  path: string
}

function getCookieFrom(name: string, cookieString: string): string | undefined {
  const matches = cookieString.match(
    new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'),
  )
  return matches ? decodeURIComponent(matches[1]) : undefined
}

function setCookie(name: string, value: string, options: Options): void {
  const expires = options.expires
  if (typeof expires === 'string') {
    // noop
  } else {
    options.expires = expires.toUTCString()
  }

  value = encodeURIComponent(value)

  let updatedCookie = name + '=' + value

  for (const propName in options) {
    updatedCookie += '; ' + propName
    const propValue = (options as any)[propName]
    if (propValue !== true) {
      updatedCookie += '=' + propValue
    }
  }

  document.cookie = updatedCookie
}

function deleteCookie(name: string, options: Omit<Options, 'expires'>): void {
  setCookie(name, '', {
    ...options,
    expires: 'Thu, 01 Jan 1970 00:00:01 GMT',
  })
}
